import type { FieldValues, Path } from 'react-hook-form';

import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSSwitch } from '@repo/sds/SDSSwitch';
import { SDSText } from '@repo/sds/SDSText';

import type { ControlledFieldProps } from '../FormField/FormField';

/** Base configuration props specific to the Switch Input component. */
export interface BaseFormSwitchInputProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed next to the switch. */
  label: string;
  /** Helper text displayed below the switch. */
  helperText?: string;
  /** Data attribute for testing purposes. */
  dataTestId?: string;
}

/** Combined props for the FormSwitchInput component, including RHF control props. */
interface FormSwitchInputProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>
  extends BaseFormSwitchInputProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {}

/**
 * Renders an SDSSwitch component integrated with React Hook Form,
 * receiving RHF state and handlers via the 'field' prop from a Controller wrapper.
 */
export function FormSwitchInput<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({ field, errorMessage, isDisabled, label, helperText, dataTestId }: FormSwitchInputProps<TFieldValues, TName>) {
  const isChecked = Boolean(field.value);

  const handleChange = () => {
    field.onChange(!isChecked);
  };

  return (
    <SDSFlexbox direction="column" gap="no-design-4" fullWidth>
      <SDSFlexbox gap="no-design-8" alignItems="center" justifyContent="space-between">
        <SDSText text={label} type="body-medium" color={isDisabled ? 'muted' : undefined} />
        <SDSSwitch
          checked={isChecked}
          onChange={handleChange}
          isDisabled={isDisabled}
          isError={Boolean(errorMessage)}
          name={field.name}
          data-testid={dataTestId}
          aria-label={label}
        />
      </SDSFlexbox>
      {helperText && <SDSText text={helperText} type="body-small" color="muted" />}
      {errorMessage && <SDSText text={errorMessage} type="body-small" color="alert" />}
    </SDSFlexbox>
  );
}
